import dayjs, { type Dayjs } from 'dayjs';
import { isStringArray } from '@/api/queries/utils';
import { FilterType } from '@/models/filter/FilterType';
import { OptionPathsByFilter } from '@/context/FiltersContext/FiltersContext';
import { isOptionPathsByFilter } from '@/utils/filter/isOptionPathsByFilter';
import { TimePeriod, type Period } from '@/types/date';
import type { FilterState } from '@/types/filter/filterState';
import { getPeriodUntilDate, getPreviousPeriod } from '@/utils/date';
import { isLocalFiltersRecord } from '@/utils/filter/isLocalFiltersRecord';
import { isRecord } from '@/utils/filter/isRecord';

const FILTER_TYPE_VALUES: ReadonlySet<string> = new Set(Object.values(FilterType));
const TIME_PERIOD_VALUES = new Set<string>(Object.values(TimePeriod));

export interface SharedViewPayload {
  globalFilters: FilterState;
  localFilters?: Record<string, Record<string, unknown>>;
  resolvedCountryCodes?: string[];
  senderCountryCodes?: string[];
  /** Option paths the hierarchical filters were selected against. */
  optionPaths?: OptionPathsByFilter;
}

export function isSharedViewFilterState(value: unknown): value is FilterState {
  if (!isRecord(value)) {
    return false;
  }

  const keys = Object.keys(value);

  return keys.every(key => FILTER_TYPE_VALUES.has(key));
}

function isTimePeriod(value: unknown): value is TimePeriod {
  return typeof value === 'string' && TIME_PERIOD_VALUES.has(value);
}

function isDayjsInputValue(value: unknown): value is string | number | Date | Dayjs {
  return typeof value === 'string' || typeof value === 'number' || value instanceof Date || dayjs.isDayjs(value);
}

function normalizePeriodValue(value: unknown): Period | null {
  if (
    !isRecord(value) ||
    !isTimePeriod(value.period) ||
    !isDayjsInputValue(value.beginn) ||
    !isDayjsInputValue(value.end)
  ) {
    return null;
  }

  const beginn = dayjs(value.beginn);
  const end = dayjs(value.end);

  if (!beginn.isValid() || !end.isValid()) {
    return null;
  }

  return { beginn, end, period: value.period };
}

export function recalculatePresetDateRanges(filters: FilterState): FilterState {
  const result = { ...filters };
  const dateRange = normalizePeriodValue(result[FilterType.DATE_RANGE]);

  if (!dateRange) {
    return result;
  }

  const comparison = normalizePeriodValue(result[FilterType.COMPARISON_PERIOD]);
  if (comparison) {
    result[FilterType.COMPARISON_PERIOD] = comparison;
  }

  if (dateRange.period === TimePeriod.CUSTOM) {
    result[FilterType.DATE_RANGE] = dateRange;
    return result;
  }

  const freshDateRange = getPeriodUntilDate(dayjs(), dateRange.period);
  result[FilterType.DATE_RANGE] = freshDateRange;

  if (comparison && comparison.period !== TimePeriod.CUSTOM) {
    result[FilterType.COMPARISON_PERIOD] = getPreviousPeriod(freshDateRange);
  }

  return result;
}

export function isSharedViewPayload(data: unknown): data is SharedViewPayload {
  if (!isRecord(data) || !('globalFilters' in data) || !isSharedViewFilterState(data.globalFilters)) {
    return false;
  }

  if ('localFilters' in data && data.localFilters !== undefined && !isLocalFiltersRecord(data.localFilters)) {
    return false;
  }

  if (
    'resolvedCountryCodes' in data &&
    data.resolvedCountryCodes !== undefined &&
    !isStringArray(data.resolvedCountryCodes)
  ) {
    return false;
  }

  if ('optionPaths' in data && data.optionPaths !== undefined && !isOptionPathsByFilter(data.optionPaths)) {
    return false;
  }

  if (
    'senderCountryCodes' in data &&
    data.senderCountryCodes !== undefined &&
    !isStringArray(data.senderCountryCodes)
  ) {
    return false;
  }

  return true;
}

export function deriveSenderCountryCodesFromLocale(locale: string): string[] {
  if (/CESAPP_ALL|CES_ADMINS/.test(locale)) {
    return [];
  }

  const matches = locale.match(/CESAPP_(\w{2})/g);
  return matches ? matches.map(match => match.slice(-2)) : [];
}
